import { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Box, Button, Card, Group, Loader, ScrollArea, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import HttpClient from '../../helpers/HttpClient';
import { useLanguage } from '../../providers/LanguageContext';
import { InvoiceSetting } from '../../types/invoice-setting.type';
import { Match } from '../../types/match.type';
import { Tag } from '../../types/tag.type';
import { TrainingEvent } from '../../types/training-event.type';
import { RentalRow } from '../../types/inventory.type';
import { USER_RIGHTS } from '../../constants/userRights';
import { useUserContext } from '../../providers/UserContext';
import { BirthdayPerson, CalendarEntry, calendarEntryKey } from './homeTypes';
import { CalendarItem } from './CalendarItem';
import { TeamFilterBadges, TrainingTagBadges } from './FilterBadges';
import { TeamFilter, toggleInSelection, writeStoredList } from './useHomeData';

const TRAINING_TAG_FILTER_KEY = 'training_tag_filter';
// Desktop: 4 weeks (1 back + current + 2 forward). Mobile: only the current
// week — the vertical agenda would otherwise be 28 days long.
const DESKTOP_WEEKS = 4;
const MOBILE_WEEKS = 1;

function mondayOf(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Calendar of matches, trainings and birthdays with team/tag filters and week navigation. */
export function CalendarSection({ trainings, teamFilter }: {
  trainings: TrainingEvent[];
  teamFilter: TeamFilter;
}) {
  const { t } = useLanguage();
  const { allTeams, selectedTeams, toggleTeam } = teamFilter;

  const [matches, setMatches] = useState<Match[]>([]);
  const [birthdays, setBirthdays] = useState<BirthdayPerson[]>([]);
  const [rentals, setRentals] = useState<RentalRow[]>([]);
  // Rentals are only shown to (and only loaded for) the inventory right.
  const { userRights } = useUserContext();
  const canSeeRentals = userRights.includes(USER_RIGHTS.INVENTORY);
  const canEditTrainings = userRights.includes(USER_RIGHTS.TRAINING);
  const canEditPersons = userRights.includes(USER_RIGHTS.PERSONS);
  const navigate = useNavigate();

  // Clicking an entry opens it for editing — only with the matching right:
  // training/event -> its edit page (training), birthday -> the person
  // (persons), rental -> the rental list (inventory; rentals are only shown
  // with that right anyway). Matches come from the ÖFB sync and aren't edited here.
  const openHandler = (item: CalendarEntry): (() => void) | undefined => {
    if (item.kind === 'training' && canEditTrainings) {
      return () => navigate(`/training/events/edit/${item.training.id}`);
    }
    if (item.kind === 'birthday' && canEditPersons) {
      return () => navigate(`/person/edit/${item.birthday.id}`);
    }
    if (item.kind === 'rental' && canSeeRentals) {
      return () => navigate('/inventory/rentals');
    }
    return undefined;
  };
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = default window (see below)
  // Same breakpoint as the agenda/grid switch below (Mantine "sm"). Read
  // synchronously so the first fetch already uses the right window size.
  const isMobile = useMediaQuery('(max-width: 48em)', undefined, { getInitialValueInEffect: false }) ?? false;
  const calendarWeeksCount = isMobile ? MOBILE_WEEKS : DESKTOP_WEEKS;
  const [clubName, setClubName] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(TRAINING_TAG_FILTER_KEY) ?? '[]'); } catch { return []; }
  });

  useEffect(() => {
    new HttpClient<InvoiceSetting>('/invoice-setting').getSingle()
      .then((s) => setClubName(s?.clubName ?? ''))
      .catch(() => {});
  }, []);

  // A rolling CALENDAR_WEEKS-week window anchored on the Monday of the current
  // week, shifted by weekOffset weeks. weekOffset=0 shows 1 week back + the
  // current week + 2 weeks forward. Refetches whenever the window moves — team
  // and tag filtering happen client-side below.
  // Desktop starts one week back; mobile starts at the current week.
  const calendarStart = new Date(mondayOf(new Date()));
  calendarStart.setDate(calendarStart.getDate() + (weekOffset - (isMobile ? 0 : 1)) * 7);
  const calendarEnd = new Date(calendarStart);
  calendarEnd.setDate(calendarStart.getDate() + calendarWeeksCount * 7 - 1);
  calendarEnd.setHours(23, 59, 59, 999);
  // Refetch when the window moves or its size changes (rotating a tablet).
  const calendarStartKey = `${calendarStart.getTime()}-${calendarWeeksCount}`;

  useEffect(() => {
    setLoading(true);
    new HttpClient<Match>('/match')
      .getAllPaged({
        skip: '0',
        take: '200',
        from: calendarStart.toISOString(),
        to: calendarEnd.toISOString(),
        sortColumn: 'matchDate',
        sortDir: 'asc',
      })
      .then((res) => setMatches(res.data ?? []))
      .catch(() => setMatches([]))
      .finally(() => setLoading(false));

    new HttpClient<BirthdayPerson>('/person/birthdays')
      .getAll({ from: calendarStart.toISOString(), to: calendarEnd.toISOString() })
      .then((res) => setBirthdays(res ?? []))
      .catch(() => setBirthdays([]));

    if (canSeeRentals) {
      new HttpClient<RentalRow>('/rental/calendar')
        .getAll({ from: calendarStart.toISOString(), to: calendarEnd.toISOString() })
        .then((res) => setRentals(res ?? []))
        .catch(() => setRentals([]));
    } else {
      setRentals([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarStartKey, canSeeRentals]);

  const allTrainingTags = useMemo(() => {
    const tagMap = new Map<number, Tag>();
    trainings.forEach((ev) => (ev.tags ?? []).forEach((tag) => tagMap.set(tag.id, tag)));
    return [...tagMap.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [trainings]);

  const toggleTag = (id: string) => {
    const next = toggleInSelection(allTrainingTags.map((tg) => tg.id.toString()), selectedTagIds, id);
    setSelectedTagIds(next);
    writeStoredList(TRAINING_TAG_FILTER_KEY, next);
  };

  const visibleMatches = selectedTeams.length === 0
    ? matches
    : matches.filter((m) => selectedTeams.includes(m.team));

  const visibleTrainings = selectedTagIds.length === 0
    ? trainings
    : trainings.filter((ev) => ev.tags?.some((tag) => selectedTagIds.includes(tag.id.toString())));

  const itemsByDay = new Map<string, CalendarEntry[]>();
  const pushItem = (dateKey: string, item: CalendarEntry) => {
    if (!itemsByDay.has(dateKey)) itemsByDay.set(dateKey, []);
    itemsByDay.get(dateKey)!.push(item);
  };
  for (const m of visibleMatches) {
    const d = dayjs(m.matchDate);
    pushItem(d.format('YYYY-MM-DD'), { kind: 'match', sortMinutes: d.hour() * 60 + d.minute(), match: m });
  }
  for (const ev of visibleTrainings) {
    pushItem(ev.date, { kind: 'training', sortMinutes: ev.startTime, training: ev });
  }
  for (const b of birthdays) {
    // Birthdays are all-day, so they sort ahead of any timed item on the same day.
    pushItem(b.date, { kind: 'birthday', sortMinutes: -1, birthday: b });
  }
  for (const r of rentals) {
    // Put a multi-day rental on each visible day of its period. The first day
    // sorts by its start time, following days like all-day entries.
    const start = dayjs(r.plannedFrom);
    const end = dayjs(r.plannedTo);
    let day = start.isBefore(calendarStart) ? dayjs(calendarStart).startOf('day') : start.startOf('day');
    const last = end.isAfter(calendarEnd) ? dayjs(calendarEnd) : end;
    while (!day.isAfter(last, 'day')) {
      const isFirst = day.isSame(start, 'day');
      pushItem(day.format('YYYY-MM-DD'), {
        kind: 'rental',
        sortMinutes: isFirst ? start.hour() * 60 + start.minute() : -1,
        rental: r,
      });
      day = day.add(1, 'day');
    }
  }
  for (const items of itemsByDay.values()) {
    items.sort((a, b) => a.sortMinutes - b.sortMinutes);
  }
  const hasAnyItems = itemsByDay.size > 0;

  const calendarWeeks: Date[][] = [];
  for (let w = 0; w < calendarWeeksCount; w++) {
    const week: Date[] = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(calendarStart);
      day.setDate(calendarStart.getDate() + w * 7 + d);
      week.push(day);
    }
    calendarWeeks.push(week);
  }
  const todayKey = dayjs().format('YYYY-MM-DD');

  return (
    <div data-tour="dashboard-calendar">
      <Group mb="xs" justify="space-between" align="flex-start" wrap="wrap">
        <Stack gap={6} data-tour="dashboard-calendar-filters">
          <Title order={3}>{t.home.matchCalendar}</Title>
          <TeamFilterBadges allTeams={allTeams} selectedTeams={selectedTeams} onToggle={toggleTeam} />
          <TrainingTagBadges allTags={allTrainingTags} selectedTagIds={selectedTagIds} onToggle={toggleTag} />
        </Stack>
        <Group gap={4} data-tour="dashboard-calendar-nav">
          <ActionIcon variant="subtle" aria-label="previous" onClick={() => setWeekOffset((o) => o - 1)}>
            <IconChevronLeft size={18} />
          </ActionIcon>
          {isMobile && (
            <Text size="xs" c="dimmed">
              {dayjs(calendarStart).format('DD.MM.')} – {dayjs(calendarEnd).format('DD.MM.')}
            </Text>
          )}
          <Button variant="subtle" size="xs" onClick={() => setWeekOffset(0)}>{t.home.today}</Button>
          <ActionIcon variant="subtle" aria-label="next" onClick={() => setWeekOffset((o) => o + 1)}>
            <IconChevronRight size={18} />
          </ActionIcon>
        </Group>
      </Group>
      {loading ? (
        <Loader size="sm" />
      ) : (
        <>
          {/* Desktop/tablet: full 7-day-wide grid, one row per week */}
          <Box visibleFrom="sm">
            <ScrollArea type="auto" offsetScrollbars>
              <Stack gap={6} miw={720}>
                {calendarWeeks.map((week, wi) => (
                  <SimpleGrid key={wi} cols={7} spacing={6}>
                    {week.map((day) => {
                      const key = dayjs(day).format('YYYY-MM-DD');
                      const dayItems = itemsByDay.get(key) ?? [];
                      const isToday = key === todayKey;
                      return (
                        <div
                          key={key}
                          style={{
                            border: `1px solid var(--mantine-color-default-border)`,
                            borderRadius: 6,
                            padding: 6,
                            minHeight: 64,
                            background: isToday ? 'var(--mantine-color-indigo-light)' : undefined,
                          }}
                        >
                          <Text size="xs" fw={isToday ? 700 : 500} c={isToday ? 'indigo' : 'dimmed'}>
                            {dayjs(day).format('ddd DD.MM.')}
                          </Text>
                          <Stack gap={2} mt={2}>
                            {dayItems.map((item) => (
                              <CalendarItem key={calendarEntryKey(item)} item={item} titleSize="xs" clubName={clubName} onOpen={openHandler(item)} />
                            ))}
                          </Stack>
                        </div>
                      );
                    })}
                  </SimpleGrid>
                ))}
              </Stack>
            </ScrollArea>
          </Box>

          {/* Mobile: a vertical agenda instead of a 7-wide grid, so nothing needs horizontal scrolling */}
          <Box hiddenFrom="sm">
            <Stack gap={4}>
              {calendarWeeks.flat().map((day) => {
                const key = dayjs(day).format('YYYY-MM-DD');
                const dayItems = itemsByDay.get(key) ?? [];
                const isToday = key === todayKey;
                return (
                  <Card
                    key={key}
                    withBorder
                    padding="xs"
                    radius="sm"
                    style={isToday ? { borderColor: 'var(--mantine-color-indigo-6)', background: 'var(--mantine-color-indigo-light)' } : undefined}
                  >
                    <Text size="sm" fw={isToday ? 700 : 600} c={isToday ? 'indigo' : undefined}>
                      {dayjs(day).format('ddd DD.MM.')}
                    </Text>
                    {dayItems.length === 0 ? (
                      <Text size="xs" c="dimmed">—</Text>
                    ) : (
                      <Stack gap={4} mt={4}>
                        {dayItems.map((item) => (
                          <CalendarItem key={calendarEntryKey(item)} item={item} titleSize="sm" clubName={clubName} onOpen={openHandler(item)} />
                        ))}
                      </Stack>
                    )}
                  </Card>
                );
              })}
            </Stack>
          </Box>
        </>
      )}
      {!loading && !hasAnyItems && (
        <Text c="dimmed" mt="xs">{t.home.noMatches}</Text>
      )}
    </div>
  );
}
